import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Chip, Kicker, Stat, GlassPanel, LogoMark } from '../components/ui';
import { NametagSticker } from '../components/Nametag';
import { supabase } from '../lib/supabase';
import { isNetworkError } from '../lib/isNetworkError';
import { getProjectFromFallback } from '../data/projectsFallback';
import { FallbackBanner } from '../components/FallbackBanner';

type ProjectStatus = 'active' | 'archived' | 'coming_soon' | 'private';

interface Project {
  id: string;
  slug: string;
  name: string;
  tagline: string | null;
  description: string | null;
  long_description: string | null;
  problem_solved: string | null;
  solution_approach: string | null;
  business_impact: string | null;
  tech_stack: string[] | null;
  technical_challenges: string[] | null;
  lessons_learned: string | null;
  project_url: string | null;
  github_url: string | null;
  demo_url: string | null;
  status: ProjectStatus | null;
  client_type: string | null;
  year_completed: number | null;
  team_size: number | null;
  my_role: string | null;
  logo_url: string | null;
  hero_image_url: string | null;
  metrics: Record<string, unknown> | null;
  created_at: string | null;
}

interface AdjacentArticle {
  slug: string;
  title: string;
  publish_date: string | null;
}

interface AdjacentLink {
  href: string;
  title: string;
  external: boolean;
}

const ADJACENT_LINK_OVERRIDES: Record<string, AdjacentLink[]> = {
  'dynamic-ally': [
    {
      href: 'https://dynamic-ally.hiimjoe.com/thesis',
      title:
        'From Procurement to Platform — a thesis on internal software for the AI-native enterprise',
      external: true,
    },
  ],
};

function metricsToTiles(metrics: Record<string, unknown> | null) {
  if (!metrics || typeof metrics !== 'object') return [];
  return Object.entries(metrics)
    .filter(([key, value]) => key !== 'category' && value !== null && value !== '' && value !== undefined)
    .slice(0, 4)
    .map(([key, value]) => {
      const label = key.replace(/_/g, ' ');
      const raw = String(value);
      // Try to split off a trailing unit/suffix like "25+", "94%", "1M".
      const unitMatch = raw.match(/^([\d.,]+)([^\d].*)?$/);
      if (unitMatch) {
        return { value: unitMatch[1], unit: unitMatch[2]?.trim(), label };
      }
      return { value: raw, unit: undefined, label };
    });
}

function statusDot(status: ProjectStatus | null | undefined) {
  if (status === 'active') return { color: 'text-ok', text: '● shipped' };
  if (status === 'archived') return { color: 'text-text-muted', text: '○ archived' };
  if (status === 'coming_soon') return { color: 'text-warn', text: '◐ early dev' };
  return { color: 'text-text-muted', text: '— unknown' };
}

function yearDisplay(p: Project): string {
  if (p.status === 'active') return p.year_completed ? `${p.year_completed}—now` : 'now';
  return p.year_completed ? String(p.year_completed) : '—';
}

const TECH_VISIBLE = 5;

const TechStackRow: React.FC<{ items: string[] }> = ({ items }) => {
  const [expanded, setExpanded] = useState(false);
  const overflow = Math.max(0, items.length - TECH_VISIBLE);
  const visible = expanded ? items : items.slice(0, TECH_VISIBLE);
  return (
    <div className="flex flex-wrap gap-s2 items-center">
      {visible.map(x => (
        <Chip key={x}>{x}</Chip>
      ))}
      {overflow > 0 && !expanded && (
        <Chip onClick={() => setExpanded(true)}>+{overflow} more</Chip>
      )}
      {expanded && overflow > 0 && (
        <Chip onClick={() => setExpanded(false)}>show less</Chip>
      )}
    </div>
  );
};

const WorkProjectPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [adjacent, setAdjacent] = useState<AdjacentArticle[]>([]);
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setProject(null);
    setNotFound(false);
    setError(null);
    setAdjacent([]);
    setUsingFallback(false);

    if (!slug) {
      setNotFound(true);
      return () => {};
    }

    (async () => {
      let hasProject = false;
      try {
        const { data, error: err } = await supabase
          .from('projects')
          .select('*')
          .eq('slug', slug)
          .neq('status', 'private')
          .maybeSingle();

        if (cancelled) return;
        if (err) throw new Error(err.message);
        if (!data) {
          setNotFound(true);
          return;
        }
        const row = data as Project;
        setProject(row);
        hasProject = true;

        const tags = [...(row.tech_stack ?? []), row.name, row.slug].filter(Boolean) as string[];
        if (tags.length === 0) return;

        const { data: articles } = await supabase
          .from('articles')
          .select('slug, title, publish_date, tags')
          .eq('status', 'published')
          .overlaps('tags', tags)
          .order('publish_date', { ascending: false })
          .limit(3);

        if (!cancelled && articles) {
          setAdjacent(
            (articles as AdjacentArticle[]).filter(a => a.slug !== null)
          );
        }
      } catch (e) {
        if (cancelled) return;
        if (isNetworkError(e)) {
          // Main project already loaded — adjacent articles just unavailable. Skip quietly.
          if (hasProject) return;
          const fb = getProjectFromFallback(slug);
          if (!fb) {
            setNotFound(true);
            return;
          }
          setProject(fb as unknown as Project);
          setUsingFallback(true);
          return;
        }
        const message = e instanceof Error ? e.message : String(e);
        setError(message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-content mx-auto px-s6 md:px-s12 py-s20 text-center">
        <div className="font-mono text-[12px] text-text-dim uppercase tracking-[0.6px] mb-s3">
          404 · not found
        </div>
        <h1 className="font-mono text-[28px] font-medium tracking-[-0.6px] text-text mb-s4">
          no project at /work/{slug}.
        </h1>
        <Link
          to="/work"
          className="font-mono text-[12px] text-accent uppercase tracking-[0.6px] hover:underline hover:decoration-accent-alt-hover hover:underline-offset-2"
        >
          ← all work
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-content mx-auto px-s6 md:px-s12 py-s12">
        <div className="font-mono text-[11px] text-err">// supabase: {error}</div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-content mx-auto px-s6 md:px-s12 py-s12">
        <div className="h-s8 w-64 bg-surface rounded-xs animate-pulse mb-s4" />
        <div className="h-s16 w-96 bg-surface rounded-xs animate-pulse" />
      </div>
    );
  }

  const tiles = metricsToTiles(project.metrics);
  const dot = statusDot(project.status);
  const problem = project.problem_solved;
  const approach = project.solution_approach;
  const outcomes = project.business_impact;
  const lesson = project.lessons_learned;
  const whatBroke = (project.technical_challenges ?? []).filter(Boolean);

  return (
    <div>
      {usingFallback && (
        <div className="max-w-content mx-auto px-s6 md:px-s12 pt-s5">
          <FallbackBanner />
        </div>
      )}
      <div className="max-w-content mx-auto px-s6 md:px-s12 pt-s5 font-mono text-[11px] text-text-dim uppercase tracking-[0.6px]">
        <Link to="/work" className="hover:text-text">work</Link>{' '}
        / <span className="text-accent">{project.name.toLowerCase()}</span>
      </div>

      <div className="max-w-content mx-auto px-s6 md:px-s12 pt-s6 pb-s8 grid md:grid-cols-[1.3fr_1fr] gap-s10 md:gap-s12">
        <div>
          <LogoMark name={project.name} logoUrl={project.logo_url} size={56} className="mb-s4" />
          <Kicker>{yearDisplay(project)}</Kicker>
          <h1 className="font-mono text-[40px] md:text-[52px] font-medium tracking-[-1.5px] leading-[1.02] mt-[14px] mb-[18px] text-text">
            {project.name}
          </h1>
          {project.tagline && (
            <p className="text-[17px] text-text-muted leading-[1.55] max-w-[520px]">
              {project.tagline}
            </p>
          )}
          {project.project_url &&
            (project.project_url.startsWith('/') ? (
              <Link
                to={project.project_url}
                className="inline-block mt-s5 font-mono text-[12px] text-accent uppercase tracking-[0.6px] hover:underline hover:decoration-accent-alt-hover hover:underline-offset-2"
              >
                visit live →
              </Link>
            ) : (
              <a
                href={project.project_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-s5 font-mono text-[12px] text-accent uppercase tracking-[0.6px] hover:underline hover:decoration-accent-alt-hover hover:underline-offset-2"
              >
                visit live ↗
              </a>
            ))}
        </div>

        {project.slug === 'joeai' ? (
          <div className="aspect-[16/10] w-full min-w-0 rounded-md border border-border bg-surface flex items-center justify-center p-s3 sm:p-s5 overflow-hidden">
            <div className="block sm:hidden">
              <NametagSticker size="sm" />
            </div>
            <div className="hidden sm:block">
              <NametagSticker size="md" />
            </div>
          </div>
        ) : project.hero_image_url ? (
          <div className="aspect-[16/10] w-full min-w-0 rounded-md border border-border overflow-hidden bg-surface">
            <img
              src={project.hero_image_url}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-[16/10] w-full min-w-0 rounded-md border border-border bg-surface flex items-center justify-center px-s3 overflow-hidden">
            <span className="font-mono text-[11px] text-text-dim uppercase tracking-[0.6px] text-center">
              // no hero image yet — coming
            </span>
          </div>
        )}
      </div>

      {project.tech_stack && project.tech_stack.length > 0 && (
        <div className="max-w-content mx-auto px-s6 md:px-s12 pb-s6">
          <TechStackRow items={project.tech_stack} />
        </div>
      )}

      {tiles.length > 0 && (
        <div className="max-w-content mx-auto px-s6 md:px-s12 pb-s12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-s4">
            {tiles.map(t => (
              <Stat key={t.label} value={t.value} unit={t.unit} label={t.label} />
            ))}
          </div>
        </div>
      )}

      <div className="max-w-content mx-auto px-s6 md:px-s12 pb-s16 grid md:grid-cols-[1fr_320px] gap-s16">
        <article className="text-[15px] leading-[1.7] text-text">
          {problem && (
            <>
              <h3 className="font-mono text-[14px] uppercase tracking-[0.6px] text-accent mb-s3">
                the problem
              </h3>
              <p className="text-text-muted whitespace-pre-line mb-s6">{problem}</p>
            </>
          )}

          {approach && (
            <>
              <h3 className="font-mono text-[14px] uppercase tracking-[0.6px] text-accent mb-s3">
                the approach
              </h3>
              <p className="text-text-muted whitespace-pre-line mb-s6">{approach}</p>
            </>
          )}

          {lesson && (
            <GlassPanel tinted className="p-s5 my-s6 border-l-[3px] border-l-accent">
              <div className="font-mono text-[10px] text-accent uppercase tracking-[1px] mb-s2">
                lesson
              </div>
              <div className="text-[16px] text-text leading-[1.5] font-medium whitespace-pre-line">
                {lesson}
              </div>
            </GlassPanel>
          )}

          {whatBroke.length > 0 && (
            <>
              <h3 className="font-mono text-[14px] uppercase tracking-[0.6px] text-accent mb-s3">
                what broke
              </h3>
              <div className="text-text-muted mb-s6 space-y-s3">
                {whatBroke.map((item, i) => (
                  <p key={i} className="whitespace-pre-line">{item}</p>
                ))}
              </div>
            </>
          )}

          {outcomes && (
            <>
              <h3 className="font-mono text-[14px] uppercase tracking-[0.6px] text-accent mb-s3">
                outcomes
              </h3>
              <p className="text-text-muted whitespace-pre-line">{outcomes}</p>
            </>
          )}

          {!problem && !approach && !outcomes && !lesson && whatBroke.length === 0 && (
            <p className="font-mono text-[12px] text-text-dim">
              // long-form write-up coming soon.
            </p>
          )}
        </article>

        <aside className="font-mono self-start">
          <div className="font-mono text-[10px] text-text-dim uppercase tracking-[1px] mb-2.5">meta</div>
          <div className="grid [grid-template-columns:80px_1fr] gap-y-2 text-[12px] text-text-muted">
            {project.my_role && (
              <>
                <span>role</span>
                <span className="text-text">{project.my_role}</span>
              </>
            )}
            {project.team_size != null && (
              <>
                <span>team</span>
                <span className="text-text">{project.team_size}</span>
              </>
            )}
            {project.tech_stack && project.tech_stack.length > 0 && (
              <>
                <span>stack</span>
                <span className="text-text truncate">
                  {project.tech_stack.slice(0, 4).join(' · ').toLowerCase()}
                </span>
              </>
            )}
            <span>status</span>
            <span className={dot.color}>{dot.text}</span>
            {project.year_completed && (
              <>
                <span>since</span>
                <span className="text-text">{project.year_completed}</span>
              </>
            )}
            {project.client_type && project.client_type !== 'company' && (
              <>
                <span>client</span>
                <span className="text-text">{project.client_type}</span>
              </>
            )}
          </div>

          <div className="h-px bg-border my-s5" />

          <div className="text-[10px] text-text-dim uppercase tracking-[1px] mb-2.5">
            adjacent writing
          </div>
          {(() => {
            const overrides = ADJACENT_LINK_OVERRIDES[project.slug];
            if (overrides && overrides.length > 0) {
              return overrides.map(link => (
                <a
                  key={link.href}
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  className="block text-[12px] text-accent mb-s2 hover:underline hover:decoration-accent-alt-hover hover:underline-offset-2"
                >
                  → {link.title}
                </a>
              ));
            }
            if (adjacent.length === 0) {
              return (
                <p className="text-[11px] text-text-dim">
                  // no adjacent writing yet — articles ship q2
                </p>
              );
            }
            return adjacent.map(a => (
              <Link
                key={a.slug}
                to={`/articles/${a.slug}`}
                className="block text-[12px] text-accent mb-s2 hover:underline hover:decoration-accent-alt-hover hover:underline-offset-2"
              >
                → {a.title}
              </Link>
            ));
          })()}
        </aside>
      </div>
    </div>
  );
};

export default WorkProjectPage;
